import React, { useEffect, useState } from "react";
import {
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Checkbox,
  TextField,
  Button,
  MenuItem,
  Select,
  styled,
  useMediaQuery,
  InputAdornment,
  CircularProgress,
  Tooltip,
  Pagination,
  FormControl,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import { useDispatch, useSelector } from "react-redux";
import ArrowDownwardIcon from "@mui/icons-material/ArrowDownward";
import SearchIcon from "@mui/icons-material/Search";
import { motion } from "framer-motion";
import { useNavigate } from "react-router-dom";
import { IconButton } from "@mui/material";
import VisibilityIcon from "@mui/icons-material/Visibility";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { format } from "date-fns";
import { AppDispatch, RootState } from "../../stores";
import { getEmbeddedUserReports } from "../../features/estimator-module/estimatorAction";
import { getMyAddWhiteLabel } from "../../features/add-white-label-module/addWhiteLabelAction";
import {
  downloadReport,
  ReportDetails,
  WhiteLabelConfig,
} from "../../utils/downloadReport";
import JSZip from "jszip";

const Container = styled(Box)(({ theme }) => ({
  minHeight: "90vh",
  background: "#fff",
  display: "flex",
  flexDirection: "row",
  overflow: "hidden",
  transform: "scale(0.95)",
  transformOrigin: "center",
  [theme.breakpoints.down("md")]: {
    flexDirection: "column",
  },
  [theme.breakpoints.down("sm")]: {
    minHeight: "85vh",
  },
}));

const Header = styled(Box)(({ theme }) => ({
  display: "flex",
  justifyContent: "space-between",
  alignItems: "center",
  marginBottom: theme.spacing(3),
  [theme.breakpoints.down("md")]: {
    flexDirection: "column",
    alignItems: "flex-start",
    gap: theme.spacing(2),
  },
}));

const SearchInput = styled(TextField)(({ theme }) => ({
  width: "200px",
  backgroundColor: "#f8f8f8",
  borderRadius: 5,
  "& .MuiOutlinedInput-root": {
    borderRadius: 8,
  },
  [theme.breakpoints.down("md")]: {
    width: "200px",
  },
  [theme.breakpoints.down("sm")]: {
    width: "100%",
  },
  "& .MuiOutlinedInput-input": {
    padding: "7px 12px",
    fontSize: "0.85rem",
    [theme.breakpoints.down("sm")]: {
      padding: "10px 12px",
      fontSize: "0.8rem",
    },
  },
}));

const ExportButton = styled(Button)(({ theme }) => ({
  textTransform: "none",
  fontWeight: 600,
  background: "linear-gradient(to top, #758d8e, #4b5b5d)",
  color: "#fff",
  borderRadius: 8,
  padding: theme.spacing(0.9, 2),
  marginLeft: theme.spacing(2),
  "&:hover": {
    backgroundColor: "#354142",
  },
  "&.Mui-disabled": {
    backgroundColor: "#bdbdbd",
  },
  [theme.breakpoints.down("md")]: {
    marginLeft: theme.spacing(1),
  },
  [theme.breakpoints.down("sm")]: {
    width: "100%",
    marginLeft: 0,
  },
}));

const StyledTable = styled(Table)(({ theme }) => ({
  minWidth: 650,
  width: "100%",
  tableLayout: "auto",
  [theme.breakpoints.down("md")]: {
    minWidth: 600,
  },
  [theme.breakpoints.down("sm")]: {
    minWidth: 500,
  },
}));

const WelcomeText = styled(Typography)(({ theme }) => ({
  fontSize: "28px",
  fontWeight: 600,
  color: "#4b5b5d",
  textAlign: "left",
  [theme.breakpoints.down("sm")]: {
    fontSize: "20px",
  },
}));

const StyledTableCell = styled(TableCell)(({ theme }) => ({
  fontSize: "0.8rem",
  color: "#4b5b5d",
  fontWeight: 500,
  padding: theme.spacing(0.3),
  whiteSpace: "nowrap",
  [theme.breakpoints.down("sm")]: {
    fontSize: "0.75rem",
    padding: theme.spacing(0.3),
  },
}));

const StyledTableHeadCell = styled(StyledTableCell)(({ theme }) => ({
  backgroundColor: "#f5f0e7",
  fontWeight: 600,
  color: "#4b5b5d",
  padding: theme.spacing(0.5),
  lineHeight: "1rem",
  whiteSpace: "nowrap",
  [theme.breakpoints.down("sm")]: {
    fontSize: "0.85rem",
    padding: theme.spacing(2),
    lineHeight: "0.5rem",
  },
}));

const DownloadSelect = styled(Select)(({ theme }) => ({
  fontSize: "0.65rem",
  height: "22px",
  borderRadius: "6px",
  backgroundColor: "#f8f8f8",
  "& .MuiSelect-select": {
    padding: "3px 8px",
    display: "flex",
    alignItems: "center",
  },
  "& .MuiOutlinedInput-notchedOutline": {
    borderColor: "#e0e0e0",
  },
  "&:hover .MuiOutlinedInput-notchedOutline": {
    borderColor: "#4b5b5d",
  },
  "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
    borderColor: "#4b5b5d",
  },
  [theme.breakpoints.down("sm")]: {
    fontSize: "0.6rem",
    height: "20px",
    "& .MuiSelect-select": {
      padding: "2px 6px",
    },
  },
}));

const FooterContainer = styled(Box)(({ theme }) => ({
  position: "fixed",
  bottom: 0,
  left: 0,
  right: 0,
  background: "#fff",
  padding: theme.spacing(2),
  zIndex: 1000,
  display: "flex",
  justifyContent: "space-between",
  alignItems: "center",
  flexWrap: "wrap",
  gap: theme.spacing(2),
  [theme.breakpoints.down("sm")]: {
    flexDirection: "column",
    padding: theme.spacing(1),
  },
}));

const DatePickerWrapper = styled(Box)(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  gap: theme.spacing(1),
  backgroundColor: "#f8f8f8",
  borderRadius: 8,
  padding: theme.spacing(1),
  "& .react-datepicker-wrapper": {
    width: "180px",
  },
  "& .react-datepicker__input-container input": {
    backgroundColor: "transparent",
    border: "1px solid #e0e0e0",
    borderRadius: "6px",
    padding: "7px",
    fontSize: "0.75rem",
    width: "100%",
    outline: "none",
    "&:focus": {
      borderColor: "#4b5b5d",
      boxShadow: "0 0 0 2px rgba(75, 91, 93, 0.2)",
    },
  },
  [theme.breakpoints.down("md")]: {
    "& .react-datepicker-wrapper": {
      width: "120px",
    },
    "& .react-datepicker__input-container input": {
      fontSize: "0.85rem",
      padding: "6px",
    },
  },
  [theme.breakpoints.down("sm")]: {
    width: "100%",
    "& .react-datepicker-wrapper": {
      width: "100%",
    },
  },
}));

// Loader Container
const LoaderContainer = styled(Box)(({ theme }) => ({
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  justifyContent: "center",
  padding: theme.spacing(6),
  minHeight: "300px",
}));

const LoaderText = styled(Typography)(({ theme }) => ({
  marginTop: theme.spacing(2),
  fontSize: "0.9rem",
  color: "#4b5b5d",
  fontWeight: 500,
}));

interface Report {
  _id: string;
  firstName: string;
  lastName: string;
  reportName: string;
  timestamp: string;
  createdAt: string;
  netProfit: number;
  utilization: number;
  utilityRate: number;
  location?: string;
  financialTable?: Array<{ category: string; value: string | number }>;
  breakEvenCashFlow?: { labels: string[]; data: number[] };
  revenueCost?: {
    labels: string[];
    datasets: Array<{ label: string; data: number[] }>;
  };
  co2Savings?: {
    labels: string[];
    datasets: Array<{ label: string; data: number[] }>;
  };
}

const formatDateToYYYYMMDD = (date: Date | null): string => {
  if (!date) return "";
  return format(date, "yyyy:MM:dd");
};

const EmbeddedReports = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const isTablet = useMediaQuery(theme.breakpoints.down("md"));
  const dispatch = useDispatch<AppDispatch>();
  const navigate = useNavigate();
  const { reports, loading, error } = useSelector(
    (state: RootState) => state.estimator
  );
  const { whiteLabel, loading: whiteLabelLoading } = useSelector(
    (state: RootState) => state.addWhiteLabel
  );
  const userData = JSON.parse(localStorage.getItem("user") || "{}");
  const currentPlanName = (
    userData?.subscription?.planName || ""
  ).toLowerCase();
  const isStarterPlan = currentPlanName === "core";
  const [selectedReports, setSelectedReports] = useState<string[]>([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [dateRange, setDateRange] = useState<[Date | null, Date | null]>([
    null,
    null,
  ]);
  const [startDate, endDate] = dateRange;
  const [formattedDateRange, setFormattedDateRange] = useState<{
    startDate: string;
    endDate: string;
  }>({ startDate: "", endDate: "" });
  const [downloadingPdf, setDownloadingPdf] = useState<string | null>(null);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [downloadType, setDownloadType] = useState<{ [key: string]: string }>(
    {}
  );

  useEffect(() => {
    setFormattedDateRange({
      startDate: formatDateToYYYYMMDD(startDate),
      endDate: formatDateToYYYYMMDD(endDate),
    });
  }, [startDate, endDate]);

  useEffect(() => {
    if (userData?.id) {
      dispatch(
        getEmbeddedUserReports({
          userId: userData.id,
          page,
          limit: pageSize,
        })
      );
    }
  }, [dispatch, page, pageSize, userData.id]);

  useEffect(() => {
    const hasWLDownload = Object.values(downloadType).includes("WL");
    if (hasWLDownload && !whiteLabel && !whiteLabelLoading) {
      dispatch(getMyAddWhiteLabel());
    }
  }, [dispatch, downloadType, whiteLabel, whiteLabelLoading]);

  const handlePageChange = (
    event: React.ChangeEvent<unknown>,
    value: number
  ) => {
    setPage(value);
  };

  const handlePageSizeChange = (event: any) => {
    setPageSize(event.target.value);
    setPage(1);
  };

  const handleSelectAllClick = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (event.target.checked) {
      const newSelected =
        reports?.reports.map((report) => report.estimator._id) || [];
      setSelectedReports(newSelected);
    } else {
      setSelectedReports([]);
    }
  };

  const handleSelectClick = (
    event: React.ChangeEvent<HTMLInputElement>,
    id: string
  ) => {
    const selectedIndex = selectedReports.indexOf(id);
    let newSelected: string[] = [];

    if (selectedIndex === -1) {
      newSelected = newSelected.concat(selectedReports, id);
    } else if (selectedIndex === 0) {
      newSelected = newSelected.concat(selectedReports.slice(1));
    } else if (selectedIndex === selectedReports.length - 1) {
      newSelected = newSelected.concat(selectedReports.slice(0, -1));
    } else if (selectedIndex > 0) {
      newSelected = newSelected.concat(
        selectedReports.slice(0, selectedIndex),
        selectedReports.slice(selectedIndex + 1)
      );
    }
    setSelectedReports(newSelected);
  };

  const isSelected = (id: string) => selectedReports.indexOf(id) !== -1;

  const filteredReports = (reports?.reports || []).filter((report) => {
    const fullName = `${report.firstName} ${report.lastName}`.toLowerCase();
    const matchesSearch =
      fullName.includes(searchTerm.toLowerCase()) ||
      report.estimator.reportName
        .toLowerCase()
        .includes(searchTerm.toLowerCase());
    return matchesSearch;
  });

  const handleDownloadPdf = async (report: Report, downloadType: string) => {
    if (!report) {
      alert("No report data to download.");
      return;
    }

    const isWhiteLabel = downloadType === "WL";
    let whiteLabelConfig: WhiteLabelConfig | undefined;
    if (isWhiteLabel && whiteLabel) {
      whiteLabelConfig = {
        brandLogo: whiteLabel.brandLogo,
        brandColor: whiteLabel.brandColor,
        textColor: whiteLabel.textColor,
        bgColor: whiteLabel.bgColor,
        domainURL: whiteLabel.domainURL || "",
        termsOfServiceURL: whiteLabel.termsOfServiceURL || "",
        privacyPolicyURL: whiteLabel.privacyPolicyURL || "",
      };
    }

    try {
      setDownloadingPdf(report._id);

      const details: ReportDetails = {
        reportName: report.reportName,
        netProfit: report.netProfit,
        address: report.location || "",
        financialTable: report.financialTable,
        breakEvenCashFlow: report.breakEvenCashFlow,
        revenueCost: report.revenueCost,
        co2Savings: report.co2Savings,
      };

      await downloadReport(details, isWhiteLabel, whiteLabelConfig, false);
    } catch (err) {
      alert("Failed to generate PDF. Please try again.");
    } finally {
      setDownloadingPdf(null);
    }
  };

  const handleDownloadMultiplePdfs = async () => {
    if (selectedReports.length === 0) {
      alert("Please select at least one report to download.");
      return;
    }

    setDownloadingPdf("multiple");

    try {
      const selectedData = (reports?.reports || []).filter((report) =>
        selectedReports.includes(report.estimator._id)
      );

      const reportPromises = selectedData.map(async (report) => {
        const isWhiteLabel = downloadType[report.estimator._id] === "WL";
        let whiteLabelConfig: WhiteLabelConfig | undefined;
        if (isWhiteLabel && whiteLabel) {
          whiteLabelConfig = {
            brandLogo: whiteLabel.brandLogo,
            brandColor: whiteLabel.brandColor,
            textColor: whiteLabel.textColor,
            bgColor: whiteLabel.bgColor,
            domainURL: whiteLabel.domainURL || "",
            termsOfServiceURL: whiteLabel.termsOfServiceURL || "",
            privacyPolicyURL: whiteLabel.privacyPolicyURL || "",
          };
        }

        const details: ReportDetails = {
          reportName: report.estimator.reportName,
          netProfit: report.estimator.netProfit,
          address: report.estimator.location || "",
          financialTable: report.estimator.financialTable,
          breakEvenCashFlow: report.estimator.breakEvenCashFlow,
          revenueCost: report.estimator.revenueCost,
          co2Savings: report.estimator.co2Savings,
        };

        const blob = await downloadReport(
          details,
          isWhiteLabel,
          whiteLabelConfig,
          true
        );
        return { blob, report: report.estimator };
      });

      const reportsWithBlobs = await Promise.all(reportPromises);

      const zip = new JSZip();
      reportsWithBlobs.forEach(({ blob, report }) => {
        const safeName = report.reportName.replace(/[^a-z0-9]+/gi, "_");
        const filename = `${safeName}_${
          downloadType[report._id] === "WL" ? "WL" : "Report"
        }.pdf`;
        zip.file(filename, blob as Blob);
      });

      const zipBlob = await zip.generateAsync({ type: "blob" });
      const url = URL.createObjectURL(zipBlob as Blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = `Reports_${new Date().toISOString().split("T")[0]}.zip`;
      link.click();
      URL.revokeObjectURL(url);
    } catch (err) {
      alert("Failed to generate ZIP file. Please try again.");
    } finally {
      setDownloadingPdf(null);
    }
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString("en-US", {
      day: "2-digit",
      month: "short",
      year: "numeric",
    });
  };

  const handleViewReport = (report: Report) => {
    navigate("/main/generate-reports/view", {
      state: { savedReport: report },
    });
  };

  return (
    <Container>
      <motion.div
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.2, ease: "easeOut" }}
        style={{
          width: "100%",
          maxWidth: "auto",
          margin: "0 auto",
          paddingBottom: "80px",
        }}
      >
        <Header>
          <WelcomeText>Embedded Reports</WelcomeText>
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: theme.spacing(isTablet ? 1 : 2),
              [theme.breakpoints.down("sm")]: {
                flexDirection: "column",
                width: "100%",
              },
            }}
          >
            <SearchInput
              placeholder="Search reports..."
              variant="outlined"
              size="small"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon />
                  </InputAdornment>
                ),
              }}
            />
            {/* <DatePickerWrapper>
              <DatePicker
                selectsRange
                startDate={startDate}
                endDate={endDate}
                onChange={(update: [Date | null, Date | null]) => {
                  setDateRange(update);
                }}
                isClearable
                placeholderText="Select date range"
                dateFormat="yyyy:MM:dd"
              />
            </DatePickerWrapper> */}
            <Box
              sx={{
                display: "flex",
                gap: 1,
                [theme.breakpoints.down("sm")]: {
                  width: "100%",
                },
              }}
            >
              <ExportButton
                variant="contained"
                disabled={
                  selectedReports.length === 0 ||
                  loading ||
                  downloadingPdf !== null
                }
                onClick={handleDownloadMultiplePdfs}
              >
                {downloadingPdf === "multiple" ? (
                  <CircularProgress size={20} sx={{ color: "#fff" }} />
                ) : (
                  "Export Report"
                )}
              </ExportButton>
            </Box>
          </Box>
        </Header>
        {loading ? (
          <LoaderContainer>
            <CircularProgress
              size={40}
              thickness={4}
              sx={{
                color: "#4b5b5d",
              }}
            />
            <LoaderText>Loading embedded reports...</LoaderText>
          </LoaderContainer>
        ) : error ? (
          <Typography color="error" variant="h6" align="center">
            {error}
          </Typography>
        ) : (
          <Box
            sx={{
              overflowX: "auto",
              "-webkit-overflow-scrolling": "touch",
              scrollbarWidth: "thin",
              "&::-webkit-scrollbar": {
                height: "8px",
              },
              "&::-webkit-scrollbar-track": {
                background: "#f1f1f1",
              },
              "&::-webkit-scrollbar-thumb": {
                background: "#4b5b5d",
                borderRadius: "4px",
              },
              "&::-webkit-scrollbar-thumb:hover": {
                background: "#354142",
              },
            }}
          >
            <StyledTable size={isMobile ? "small" : "medium"}>
              <TableHead sx={{ background: "#f5f0e7" }}>
                <TableRow>
                  <StyledTableHeadCell padding="checkbox">
                    <Checkbox
                      sx={{
                        color: "#4b5b5d",
                        "&.Mui-checked": {
                          color: "#4b5b5d",
                        },
                      }}
                      size="small"
                      indeterminate={
                        selectedReports.length > 0 &&
                        selectedReports.length < (reports?.reports.length || 0)
                      }
                      //   checked={
                      //     reports?.reports.length > 0 &&
                      //     selectedReports.length === reports?.reports.length
                      //   }
                      onChange={handleSelectAllClick}
                      disabled={loading}
                    />
                  </StyledTableHeadCell>
                  <StyledTableHeadCell>Full Name</StyledTableHeadCell>
                  <StyledTableHeadCell>Report Name</StyledTableHeadCell>
                  <StyledTableHeadCell>Location</StyledTableHeadCell>
                  <StyledTableHeadCell>Net Profit</StyledTableHeadCell>
                  <StyledTableHeadCell>Generated On</StyledTableHeadCell>
                  <StyledTableHeadCell>Actions</StyledTableHeadCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {filteredReports.length === 0 ? (
                  <TableRow>
                    <StyledTableCell colSpan={7} align="center" sx={{ py: 3 }}>
                      <Typography variant="h6" color="#4b5b5d" fontWeight={500}>
                        No Data Available
                      </Typography>
                    </StyledTableCell>
                  </TableRow>
                ) : (
                  filteredReports.map((report) => {
                    const truncatedReportName =
                      report.estimator.reportName.length > 40
                        ? `${report.estimator.reportName.slice(0, 40)}...`
                        : report.estimator.reportName;
                    const isItemSelected = isSelected(report.estimator._id);
                    return (
                      <TableRow
                        key={report.estimator._id}
                        selected={isItemSelected}
                        sx={{
                          "&:nth-of-type(odd)": { backgroundColor: "#f8f8f8" },
                          "&:hover": { backgroundColor: "#e0e0e0" },
                        }}
                      >
                        <StyledTableCell padding="checkbox">
                          <Checkbox
                            size="small"
                            checked={isItemSelected}
                            onChange={(event) =>
                              handleSelectClick(event, report.estimator._id)
                            }
                            sx={{
                              color: "#4b5b5d",
                              "&.Mui-checked": {
                                color: "#4b5b5d",
                              },
                            }}
                            disabled={loading}
                          />
                        </StyledTableCell>
                        <StyledTableCell>
                          {`${report.firstName} ${report.lastName}`}
                        </StyledTableCell>
                        <StyledTableCell>{truncatedReportName}</StyledTableCell>
                        <StyledTableCell>
                          {report.estimator.location || "N/A"}
                        </StyledTableCell>
                        <StyledTableCell>
                          {"$"}
                          {report.estimator.netProfit}
                        </StyledTableCell>
                        <StyledTableCell>
                          {formatDate(report.estimator.createdAt)}
                        </StyledTableCell>
                        <StyledTableCell>
                          <Box
                            sx={{
                              display: "flex",
                              gap: isMobile ? 1 : 0,
                              flexDirection: "row",
                              alignItems: "center",
                            }}
                          >
                            <Tooltip title="View Report">
                              <IconButton
                                size={isMobile ? "small" : "medium"}
                                onClick={() =>
                                  handleViewReport(
                                    report.estimator as unknown as Report
                                  )
                                }
                                disabled={loading}
                                sx={{ color: "#4b5b5d" }}
                              >
                                <VisibilityIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                            <Tooltip title="Download PDF">
                              <IconButton
                                size={isMobile ? "small" : "medium"}
                                onClick={() =>
                                  handleDownloadPdf(
                                    report.estimator as unknown as Report,
                                    downloadType[report.estimator._id] || "PDF"
                                  )
                                }
                                disabled={
                                  loading ||
                                  downloadingPdf === report.estimator._id
                                }
                                sx={{ color: "#4b5b5d" }}
                              >
                                {downloadingPdf === report.estimator._id ? (
                                  <CircularProgress
                                    size={16}
                                    sx={{ color: "#4b5b5d" }}
                                  />
                                ) : (
                                  <ArrowDownwardIcon fontSize="small" />
                                )}
                              </IconButton>
                            </Tooltip>
                            {!isStarterPlan && (
                              <DownloadSelect
                                value={
                                  downloadType[report.estimator._id] || "PDF"
                                }
                                onChange={(e) =>
                                  setDownloadType({
                                    ...downloadType,
                                    [report.estimator._id]: e.target
                                      .value as string,
                                  })
                                }
                                disabled={
                                  loading ||
                                  downloadingPdf === report.estimator._id
                                }
                              >
                                <MenuItem
                                  value="PDF"
                                  sx={{ fontSize: "0.65rem" }}
                                >
                                  PDF
                                </MenuItem>
                                <MenuItem
                                  value="WL"
                                  sx={{ fontSize: "0.65rem" }}
                                >
                                  WL PDF
                                </MenuItem>
                              </DownloadSelect>
                            )}
                          </Box>
                        </StyledTableCell>
                      </TableRow>
                    );
                  })
                )}
              </TableBody>
            </StyledTable>
          </Box>
        )}
        {!loading && (
          <FooterContainer>
            <Typography
              variant="body2"
              color="#4b5b5d"
              sx={{
                display: "flex",
                justifyContent: isMobile ? "center" : "flex-end",
                width: isMobile ? "100%" : "auto",
              }}
            >
              Showing {(page - 1) * pageSize + 1} to{" "}
              {Math.min(page * pageSize, reports?.total || 0)} of{" "}
              {reports?.total || 0} entries
            </Typography>

            {!isMobile && (
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                  flexWrap: "wrap",
                  gap: 2,
                }}
              >
                <Typography variant="body2" color="#4b5b5d">
                  Show:
                </Typography>
                <FormControl size="small" sx={{ minWidth: 80 }}>
                  <Select
                    value={pageSize}
                    onChange={handlePageSizeChange}
                    sx={{
                      "& .MuiSelect-select": {
                        padding: "4px 8px",
                        fontSize: "0.8rem",
                      },
                    }}
                  >
                    <MenuItem value={10}>10</MenuItem>
                    <MenuItem value={20}>20</MenuItem>
                    <MenuItem value={50}>50</MenuItem>
                  </Select>
                </FormControl>
                <Typography variant="body2" color="#4b5b5d">
                  entries
                </Typography>
              </Box>
            )}

            <Pagination
              count={reports?.pages || 1}
              page={page}
              onChange={handlePageChange}
              color="primary"
              size={isMobile ? "small" : "medium"}
              showFirstButton
              showLastButton
              sx={{
                display: "flex",
                justifyContent: isMobile ? "center" : "flex-end",
                width: isMobile ? "100%" : "auto",
                "& .MuiPaginationItem-root": {
                  color: "#4b5b5d",
                  "&.Mui-selected": {
                    backgroundColor: "#758d88",
                    color: "#f8f9fa",
                    fontWeight: 600,
                    "&:hover": {
                      backgroundColor: "#4b5b5d",
                    },
                  },
                  "&:hover": {
                    backgroundColor: "rgba(75, 91, 93, 0.04)",
                  },
                },
              }}
            />
          </FooterContainer>
        )}
      </motion.div>
    </Container>
  );
};

export default EmbeddedReports;
